import { Trans, useTranslation } from 'react-i18next';

import { DateAdapter } from '@myphx-engineering/date-utils/client';
import {
    Alert,
    Body1,
    Button,
    Modal,
    Stack,
    Subtitle1,
} from '@myphx-engineering/phx-design-system';

export type DrxMismatchModalProps = {
    opened: boolean;
    name: string;
    dateOfBirth: string;
    phoneNumber: string;
    email: string;
    onMakeChanges: () => void;
    onContinue: () => Promise<void>;
};

export const DrxMismatchModal = ({
    opened,
    name,
    dateOfBirth,
    phoneNumber,
    email,
    onMakeChanges,
    onContinue,
}: DrxMismatchModalProps) => {
    const { t, i18n } = useTranslation();

    const date = DateAdapter.plainDateFromString(dateOfBirth);
    const locale = i18n.language === 'en' ? 'en-US' : 'es-US';

    return (
        <Modal
            opened={opened}
            title={t('drxMismatchModal.title')}
            onClose={onMakeChanges}
            size="lg"
        >
            <Stack gap="lg">
                <Alert severity="warning">
                    <Trans
                        i18nKey="drxMismatchModal.alert"
                        components={{ 1: <strong /> }}
                    />
                </Alert>
                <Stack gap="xl">
                    <Subtitle1>{t('drxMismatchModal.description')}</Subtitle1>
                    <Stack gap="md">
                        <Stack gap="xxs">
                            <Body1 bold>
                                {t('drxMismatchModal.fields.name')}
                            </Body1>
                            <Body1>{name}</Body1>
                        </Stack>
                        <Stack gap="xxs">
                            <Body1 bold>
                                {t('drxMismatchModal.fields.dateOfBirth')}
                            </Body1>
                            <Body1>
                                {DateAdapter.formatPlainDate(date, locale, {
                                    dateStyle: 'long',
                                })}
                            </Body1>
                        </Stack>
                        <Stack gap="xxs">
                            <Body1 bold>
                                {t('drxMismatchModal.fields.phoneNumber')}
                            </Body1>
                            <Body1>{phoneNumber}</Body1>
                        </Stack>
                        <Stack gap="xxs">
                            <Body1 bold>
                                {t('drxMismatchModal.fields.emailAddress')}
                            </Body1>
                            <Body1>{email}</Body1>
                        </Stack>
                    </Stack>
                    <Stack gap="xs">
                        <Subtitle1>
                            {t('drxMismatchModal.someoneElse.title')}
                        </Subtitle1>
                        <Body1>{t('drxMismatchModal.someoneElse.body')}</Body1>
                    </Stack>
                </Stack>
                <Button size="lg" onClick={onMakeChanges}>
                    {t('drxMismatchModal.buttons.makeChanges')}
                </Button>
                <Button size="lg" variant="outline" onClick={onContinue}>
                    {t('drxMismatchModal.buttons.continueWithCurrentInfo')}
                </Button>
            </Stack>
        </Modal>
    );
};
